import React, { useEffect, useState, useRef } from 'react';
import { getFirestore, collection, onSnapshot, query, orderBy, addDoc, serverTimestamp, where } from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { Button, Form, Spinner } from 'react-bootstrap';
import { FaArrowLeft, FaArrowRight } from 'react-icons/fa';


import './xDocList.css';

const XArtist = () => {
    const [artists, setArtists] = useState([]);
    const [selectedDate, setSelectedDate] = useState("");
    const [rsvpStatus, setRsvpStatus] = useState(null);
    const [focusedArtistId, setFocusedArtistId] = useState(null);
    const [rsvpCount, setRsvpCount] = useState(0);
    const [currentUser, setCurrentUser] = useState({ name: "Anonymous", email: "Anonymous Member" });

    const scrollRef = useRef(null);
    const db = getFirestore();
    const auth = getAuth();

    // AUTH PERSISTENCE
    useEffect(() => {
        const unsubAuth = onAuthStateChanged(auth, (user) => {
            if (user) {
                const userData = {
                    name: user.displayName || "Member",
                    email: user.email || "No Email"
                };
                setCurrentUser(userData);
                sessionStorage.setItem("user", userData.name);
                sessionStorage.setItem("email", userData.email);
            }
        });
        return () => unsubAuth();
    }, [auth]);

    // FETCH ARTISTS
    useEffect(() => {
        const q = query(collection(db, "xArtistz"), orderBy("createdAt", "desc"));
        return onSnapshot(q, (snap) => {
            const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            setArtists(data);

            const featuredArtist = data.find(a => a.isFeatured === true) ||
                data.find(a => a.isActive === true && a.status === 'active') ||
                data.find(a => a.status === 'active');

            if (featuredArtist && !focusedArtistId) {
                setFocusedArtistId(featuredArtist.id);
                if (featuredArtist.performanceDate) {
                    setSelectedDate(featuredArtist.performanceDate.split(',')[0].trim());
                }
            }
        });
    }, [db, focusedArtistId]);

    // RSVP COUNT
    useEffect(() => {
        if (!focusedArtistId || !selectedDate) {
            setRsvpCount(0);
            return;
        }
        const rsvpQuery = query(
            collection(db, "xRSVPs"),
            where("artistId", "==", focusedArtistId),
            where("eventDate", "==", selectedDate)
        );
        return onSnapshot(rsvpQuery, (snap) => {
            setRsvpCount(snap.size);
        });
    }, [db, focusedArtistId, selectedDate]);

    const activeList = artists.filter(a => a.status === 'active' && a.isActive === true);
    const spotlight = artists.find(a => a.id === focusedArtistId) || activeList[0];

    const handleRSVP = async () => {
        if (!selectedDate || !spotlight) return;
        setRsvpStatus('loading');
        try {
            await addDoc(collection(db, "xRSVPs"), {
                artistId: spotlight.id,
                artistName: spotlight.title,
                eventDate: selectedDate,
                userName: currentUser.name,
                userEmail: currentUser.email,
                createdAt: serverTimestamp()
            });
            setRsvpStatus('success');
            setTimeout(() => setRsvpStatus(null), 4000);
        } catch (e) {
            console.error(e);
            setRsvpStatus('error');
        }
    };

    const handleSwitchArtist = (artist) => {
        setFocusedArtistId(artist.id);
        if (artist.performanceDate) {
            setSelectedDate(artist.performanceDate.split(',')[0].trim());
        }
    };

    const scroll = (direction) => {
        if (scrollRef.current) {
            const { scrollLeft } = scrollRef.current;
            const scrollAmount = 240;
            const scrollTo = direction === 'left' ? scrollLeft - scrollAmount : scrollLeft + scrollAmount;
            scrollRef.current.scrollTo({ left: scrollTo, behavior: 'smooth' });
        }
    };

    if (!spotlight) return null;
    const dateOptions = spotlight.performanceDate ? spotlight.performanceDate.split(',') : [];

    const renderMedia = (url, isThumbnail = false) => {
        if (url) {
            return (
                <div className={isThumbnail ? "carousel-img-wrapper" : "img-wrapper"}>
                    <img src={url} alt="artist" className="img-fluid" style={isThumbnail ? { height: '100%', objectFit: 'cover' } : {}} />
                </div>
            );
        }
        return (
            <div className={isThumbnail ? "carousel-img-wrapper bg-dark" : "fallback-icon-container w-100 h-100"}>
                <i className={`bi bi-person-fill text-secondary`} style={{ fontSize: isThumbnail ? '1.2rem' : '4rem' }}></i>
            </div>
        );
    };

    return (
        <div className="xArtist-spotlight mb-1">
            <style>{`
                .artist-card-container { 
                    min-height: 280px; 

               
                    backdrop-filter: blur(15px);
                    -webkit-backdrop-filter: blur(15px);
                    border: 1px solid rgba(101, 136, 219, 0.3);
                    border-radius:6px;
                    overflow: hidden; 
                }

             
                .spotlight-img-container { padding: 25px; display: flex; align-items: center; justify-content: left; }
                .img-wrapper { max-width: 220px; width: 100%; overflow: hidden; border-radius: 12px; border: 1px solid rgba(255,255,255,0.1); }
                .img-wrapper img { width: 100%; transition: transform 0.5s ease; }
                .img-wrapper:hover img { transform: scale(1.08); }
                .rsvp-badge-count { background: rgba(13, 202, 240, 0.1); color: #0dcaf0; padding: 2px 8px; border-radius: 4px; font-size: 10px; border: 1px solid rgba(13, 202, 240, 0.2); }
                
                .btn-rsvp-custom { 
                    background: #0dcaf0 !important; 
                    color: #000 !important; 
                    font-weight: 900 !important; 
                    font-size: 12px !important;
                    height: 40px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    letter-spacing: 1px; 
                }
                .btn-rsvp-success { background: #198754 !important; color: white !important; animation: rsvpPulse 1.5s infinite; }
                
                /* Carousel Logic */
                .carousel-wrapper-relative { 
                    position: relative; 
                    display: flex; 
                    align-items: center; 
                    mask-image: linear-gradient(to right, transparent, black 10%, black 90%, transparent);
                    -webkit-mask-image: linear-gradient(to right, transparent, black 10%, black 90%, transparent);
                }

                .artist-carousel-container { 
                    scroll-behavior: smooth; 
                    display: flex;
                    gap: 1.5rem;
                    overflow-x: auto;
                    padding: 15px 0;
                    -ms-overflow-style: none;
                    scrollbar-width: none;
                }
                .artist-carousel-container::-webkit-scrollbar { display: none; }

                .nav-arrow {
                    background: rgba(40, 40, 40, 0.5); 
                    border: 1px solid rgba(211, 211, 211, 0.4);
                    color: #ffc107;
                    border-radius: 50%;
                    width: 36px;
                    height: 36px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    position: absolute;
                    top: 50%;
                    transform: translateY(-50%);
                    z-index: 20;
                    cursor: pointer;
                    opacity: 0.7;
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                }
                .nav-arrow:hover { 
                    opacity: 1; 
                    background: rgba(40, 40, 40, 0.6); 
                    border-color: #ffc107;
                    transform: translateY(-50%) scale(1.1);
                }
                .arrow-left { left: 5px; }
                .arrow-right { right: 5px; }

                @media (hover: none) { .nav-arrow { display: none; } }

                .carousel-img-wrapper { 
                    width: 100px; 
                    height: 100px; 
                    border-radius: 50%; 
                    overflow: hidden; 
                    border: 2px solid rgba(245, 141, 23, 0.99); 
                    display: flex; 
                    align-items: center; 
                    justify-content: center;
                    transition: all 0.3s ease;
                }
                .artist-item:hover .carousel-img-wrapper { border-color: rgba(13, 202, 240, 0.6); }
                .artist-item.active .carousel-img-wrapper { border-color: #0dcaf0; box-shadow: 0 0 15px rgba(13, 202, 240, 0.4); }

                @keyframes rsvpPulse {
                    0% { box-shadow: 0 0 0 0 rgba(25, 135, 84, 0.7); }
                    70% { box-shadow: 0 0 0 10px rgba(25, 135, 84, 0); }
                    100% { box-shadow: 0 0 0 0 rgba(25, 135, 84, 0); }
                }
            `}</style>

            <div className="d-flex align-items-center justify-content-between m-1 px-2 py-1 ">
                <h4 className="orange fw-bold" style={{ letterSpacing: '1.5px', fontSize: '1rem', marginLeft: '20px' }}>SPOTLIGHT</h4>
                <span style={{ background: '#ffc107', color: '#000', fontSize: '9px', padding: '2px 8px', borderRadius: '4px', fontWeight: '900' }}>FEATURED</span>
            </div>

            <div className="artist-card-container m-4 rounded-4 ">
                <div className="row g-0 align-items-center">
                    <div className="col-md-3 spotlight-img-container">
                        {renderMedia(spotlight.fileUrl)}
                    </div>
                    <div className="col-md-9 p-4 spotlight-content">
                        <div className="d-flex justify-content-between align-items-start">
                            <div>
                                <h2 className="text-info fw-bold mb-0">{spotlight.title}</h2>
                                {spotlight.externalLink && (
                                    <a href={spotlight.externalLink} target="_blank" rel="noreferrer" className="text-muted-50 smallest text-decoration-none d-block mt-1">
                                        Visit Website <i className="bi bi-box-arrow-up-right ms-1" style={{ fontSize: '10px' }}></i>
                                    </a>
                                )}
                                <span className="rsvp-badge-count fw-bold mt-2 d-inline-block">
                                    <i className="bi bi-people-fill me-1"></i>{rsvpCount} GOING
                                </span>
                            </div>
                        </div>
                        <hr className="border-secondary opacity-25 my-2" />
                        <div style={{ maxHeight: '80px', overflowY: 'auto' }}>
                            <p className="text-muted-50 mb-0" style={{ fontSize: '0.9rem' }}>{spotlight.bio}</p>
                        </div>
                        <div className="rsvp-section mt-4">
                            <Form.Label className="text-info fw-bold mb-1" style={{ fontSize: '10px' }}>RESERVE YOUR SPOT</Form.Label>
                            <div className="d-flex gap-2">
                                <Form.Select
                                    className=" text-info border-secondary shadow-none"
                                    style={{
                                        fontSize: '13px',
                                        height: '40px',
                                        paddingTop: '0',
                                        paddingBottom: '0',
                                        maxWidth: '180px',
                                        background: 'rgba(255,255,255,0.05)'
                                    }}
                                    value={selectedDate}
                                    onChange={(e) => setSelectedDate(e.target.value)}
                                >
                                    {dateOptions.map((d, i) => <option key={i} value={d.trim()}>{d.trim()}</option>)}
                                </Form.Select>
                                <Button
                                    className={`btn-rsvp-custom px-4 ${rsvpStatus === 'success' ? 'btn-rsvp-success' : ''}`}
                                    onClick={handleRSVP}
                                    disabled={rsvpStatus === 'loading' || rsvpStatus === 'success'}
                                >
                                    {rsvpStatus === 'loading' ? <Spinner size="sm" /> : rsvpStatus === 'success' ? 'BOOKED!' : 'RSVP'}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {activeList.length > 1 && (
                <div className="mt-4 px-4" style={{ position: 'relative' }}>
                    <h6 className="text-muted fw-bold mb-2 text-uppercase" style={{ letterSpacing: '2px', fontSize: '9px' }}>More Artists</h6>

                    <div className="nav-arrow arrow-left" onClick={() => scroll('left')}>


                        <FaArrowLeft></FaArrowLeft>

                    </div>

                    <div className="carousel-wrapper-relative">
                        <div className="artist-carousel-container" ref={scrollRef}>
                            {activeList.map(art => (
                                <div
                                    key={art.id}
                                    className={`artist-item text-center ${focusedArtistId === art.id ? 'active' : ''}`}
                                    onClick={() => handleSwitchArtist(art)}
                                    style={{ cursor: 'pointer', minWidth: '70px' }}
                                >
                                    <div className="d-flex justify-content-center">
                                        {renderMedia(art.fileUrl, true)}
                                    </div>
                                    <div className={`smallest fw-bold mt-2 text-truncate ${focusedArtistId === art.id ? 'orange' : 'text-muted'}`} style={{ width: '80px', fontSize: '9px' }}>
                                        {art.title}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="nav-arrow arrow-right" onClick={() => scroll('right')}>
                        <FaArrowRight></FaArrowRight>
                    </div>
                </div>
            )}
        </div>
    );
};

export default XArtist;